import React from 'react';
import { Pressable, StyleSheet, Text, View, ViewStyle } from 'react-native';
import Svg, { Circle, Defs, Line, Polygon, RadialGradient, Rect, Stop } from 'react-native-svg';
import { useTheme, makeThemedStyles, radii } from '../v10/theme';
import { Atmosphere, GlassFill } from './glass';

// Layout constants (formerly tokens.sizes).
const sizes = { gearTile: 44, navShape: 56, hexagon: 28 } as const;

/**
 * CloudBackdrop — an actual sky, not a 3-stop linear gradient.
 *
 * `expo-linear-gradient` only draws straight bands, which reads as "tinted
 * grey" rather than "heavenly." This layers several soft RADIAL glows
 * (SVG, since RN has no native radial-gradient) at staggered positions and
 * radii over a pale-sky base — the same trick illustrators use for cloud
 * light: overlapping soft white blooms, denser near the top, thinning out
 * toward the edges.
 *
 * v10: dark mode renders the navy page gradient + gold radiance (no clouds);
 * light mode is a subtle ivory wash with faint blooms. Purely atmospheric.
 */
export function CloudBackdrop({ style }: { style?: ViewStyle | ViewStyle[] }) {
  const { t, isDark } = useTheme();
  if (isDark) return <Atmosphere />;
  return (
    <View style={[StyleSheet.absoluteFill, style]} pointerEvents="none">
      <Svg width="100%" height="100%" viewBox="0 0 400 800" preserveAspectRatio="xMidYMid slice">
        <Defs>
          <RadialGradient id="sky" cx="50%" cy="0%" r="90%">
            <Stop offset="0%" stopColor={t.navy} stopOpacity={1} />
            <Stop offset="45%" stopColor={t.night} stopOpacity={1} />
            <Stop offset="100%" stopColor={t.night} stopOpacity={1} />
          </RadialGradient>
          <RadialGradient id="bloomA" cx="50%" cy="50%" r="50%">
            <Stop offset="0%" stopColor={t.navyRaised} stopOpacity={0.5} />
            <Stop offset="60%" stopColor={t.navyRaised} stopOpacity={0.18} />
            <Stop offset="100%" stopColor={t.navyRaised} stopOpacity={0} />
          </RadialGradient>
          <RadialGradient id="bloomB" cx="50%" cy="50%" r="50%">
            <Stop offset="0%" stopColor={t.navy} stopOpacity={0.45} />
            <Stop offset="70%" stopColor={t.navy} stopOpacity={0.15} />
            <Stop offset="100%" stopColor={t.navy} stopOpacity={0} />
          </RadialGradient>
        </Defs>
        <Rect x={0} y={0} width={400} height={800} fill="url(#sky)" />
        {/* Staggered soft blooms — like light diffusing through cloud cover. */}
        <Circle cx={90} cy={60} r={190} fill="url(#bloomA)" />
        <Circle cx={330} cy={140} r={150} fill="url(#bloomB)" />
        <Circle cx={40} cy={260} r={130} fill="url(#bloomB)" />
        <Circle cx={300} cy={330} r={170} fill="url(#bloomA)" />
        <Circle cx={150} cy={480} r={200} fill="url(#bloomB)" />
        <Circle cx={350} cy={620} r={160} fill="url(#bloomA)" />
      </Svg>
    </View>
  );
}

/**
 * MoonGlow — a soft luminous circle tucked into one corner, like a moon
 * behind cloud. Pairs with CloudBackdrop for the "quote card" heavenly look
 * (the reference: a sky+moon card behind a short italic quote).
 * v10: renders nothing in dark mode, a faint wash in light.
 */
export function MoonGlow({ side = 'right' }: { side?: 'left' | 'right' }) {
  const { t, isDark } = useTheme();
  if (isDark) return null;
  const offset = side === 'right' ? { right: -30 } : { left: -30 };
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <View style={[{ position: 'absolute', top: -20, width: 110, height: 110, borderRadius: 55, backgroundColor: t.navyRaised, opacity: 0.5 }, offset]} />
      <View style={[{ position: 'absolute', top: -45, width: 160, height: 160, borderRadius: 80, backgroundColor: t.goldGlow, opacity: 0.18 }, side === 'right' ? { right: -55 } : { left: -55 }]} />
    </View>
  );
}

// Pentagon (STAGE) and Triangle (SHAPE) — viewBox 100x100, matching the web SVGs.
const PENTAGON_POINTS = '50,4 97,38 79,94 21,94 3,38';
const TRIANGLE_POINTS = '50,8 96,92 4,92';
const HEXAGON_POINTS = '50,3 93,26 93,74 50,97 7,74 7,26';

interface ShapeProps {
  label: string;
  active?: boolean;
  size?: number;
  onPress?: () => void;
}

function ShapeButton({
  points,
  label,
  active,
  size = sizes.navShape,
  onPress,
}: ShapeProps & { points: string }) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} style={[styles.shapeWrap, active && neu.glowPrimary]}>
      <Svg width={size} height={size} viewBox="0 0 100 100">
        <Polygon
          points={points}
          fill={active ? c.lime : t.glass}
          stroke={active ? t.gold : t.navyLine}
          strokeWidth={5}
          strokeLinejoin="round"
        />
      </Svg>
      <Text
        style={[
          styles.shapeLabel,
          { color: active ? t.goldBright : t.ivorySoft },
        ]}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export function Pentagon(props: ShapeProps) {
  return <ShapeButton {...props} points={PENTAGON_POINTS} />;
}

export function Triangle(props: ShapeProps) {
  return <ShapeButton {...props} points={TRIANGLE_POINTS} />;
}

export function Hexagon({
  letter,
  active,
  size = sizes.hexagon,
  onPress,
}: {
  letter: string;
  active?: boolean;
  size?: number;
  onPress?: () => void;
}) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} style={[styles.hexWrap, active && neu.glowPrimary]}>
      <Svg width={size} height={size} viewBox="0 0 100 100">
        <Polygon
          points={HEXAGON_POINTS}
          fill={active ? c.lime : t.glass}
          stroke={active ? t.gold : t.ivorySoft}
          strokeWidth={7}
          strokeLinejoin="round"
        />
      </Svg>
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <View style={styles.hexCenter}>
          <Text style={[styles.hexLetter, { color: active ? t.goldBright : t.ivorySoft }]}>
            {letter}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

export function GearTile({
  letter,
  active,
  onPress,
}: {
  letter: string;
  active?: boolean;
  onPress?: () => void;
}) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.gearTile,
        { backgroundColor: active ? c.lime : t.glass, borderColor: active ? t.gold : t.navyLine },
        active ? neu.glowPrimary : neu.raisedSm,
      ]}
    >
      <Text style={[styles.gearLetter, { color: active ? t.goldBright : t.ivorySoft }]}>
        {letter}
      </Text>
    </Pressable>
  );
}

export function PromptPill({
  text,
  active,
  style,
}: {
  text: string;
  active?: boolean;
  style?: ViewStyle;
}) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  return (
    <View
      style={[
        styles.pill,
        { backgroundColor: active ? c.lime : t.glass, borderColor: active ? t.gold : t.navyLine },
        active ? neu.glowPrimary : neu.raisedSm,
        style,
      ]}
    >
      <Text style={[styles.pillText, { color: active ? t.goldBright : t.ivorySoft }]}>
        {text}
      </Text>
    </View>
  );
}

// Landscape collapse buttons: clock = ORDER, ASTRA = navigation.
export function ClockIcon({ size = 26 }: { size?: number }) {
  const { c } = useTheme();
  return (
    <Svg width={size} height={size} viewBox="0 0 100 100">
      <Circle cx={50} cy={50} r={42} fill="none" stroke={c.text} strokeWidth={9} />
      <Line x1={50} y1={50} x2={50} y2={24} stroke={c.text} strokeWidth={9} strokeLinecap="round" />
      <Line x1={50} y1={50} x2={70} y2={60} stroke={c.text} strokeWidth={9} strokeLinecap="round" />
    </Svg>
  );
}

export function CompassIcon({ size = 26 }: { size?: number }) {
  const { c } = useTheme();
  return (
    <Svg width={size} height={size} viewBox="0 0 100 100">
      <Circle cx={50} cy={50} r={42} fill="none" stroke={c.text} strokeWidth={8} />
      <Polygon
        points="50,14 58,42 86,50 58,58 50,86 42,58 14,50 42,42"
        fill={c.text}
      />
      <Circle cx={50} cy={50} r={7} fill={c.lime} stroke={c.text} strokeWidth={3} />
    </Svg>
  );
}

export function GlassCircle({
  children,
  onPress,
  size = 52,
}: {
  children: React.ReactNode;
  onPress?: () => void;
  size?: number;
}) {
  const styles = useStyles();
  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.glassCircle,
        { width: size, height: size, borderRadius: size / 2 },
      ]}
    >
      <GlassFill strong radius={size / 2} />
      {children}
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  shapeWrap: { alignItems: 'center', justifyContent: 'center' },
  shapeLabel: {
    position: 'absolute',
    fontSize: 11,
    fontWeight: '800',
    textAlign: 'center',
    top: '42%',
  },
  hexWrap: { alignItems: 'center', justifyContent: 'center' },
  hexCenter: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  hexLetter: { fontSize: 12, fontWeight: '900' },
  gearTile: {
    width: sizes.gearTile,
    height: sizes.gearTile,
    borderRadius: radii.tile,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  gearLetter: { fontSize: 18, fontWeight: '900' },
  pill: {
    borderRadius: radii.pill,
    borderWidth: 1,
    paddingHorizontal: 18,
    paddingVertical: 12,
    flex: 1,
    justifyContent: 'center',
  },
  pillText: { fontSize: 14, fontWeight: '700', fontStyle: 'italic' },
  glassCircle: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: t.navyLine,
    alignItems: 'center',
    justifyContent: 'center',
    ...neu.raisedSm,
  },
  }),
);
